'use client';

import { useState, useEffect } from 'react';
import { Alert, Button, Stack, Text, Group } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';

interface EmailVerificationStepProps {
  userEmail?: string;
  emailVerificationSent: boolean;
  emailVerified: boolean;
  onSendVerification: () => void;
  onCheckVerification: () => void;
  authError?: string;
  loading?: boolean;
}

export default function EmailVerificationStep({
  userEmail,
  emailVerificationSent,
  emailVerified,
  onSendVerification,
  onCheckVerification,
  authError,
  loading = false
}: EmailVerificationStepProps) {
  const [isPolling, setIsPolling] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendMessage, setResendMessage] = useState<string | null>(null);

  // Use window focus event to check verification status
  useEffect(() => {
    const handleWindowFocus = async () => {
      if (emailVerificationSent && !emailVerified && !loading) {
        onCheckVerification();
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [emailVerificationSent, emailVerified, onCheckVerification, loading]);

  // Cooldown timer effect
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Handle resend verification email
  const handleResendEmail = async () => {
    if (resendCooldown > 0 || resendLoading) return;
    
    setResendLoading(true);
    setResendMessage(null);
    
    try {
      const response = await fetch('/api/auth/verify-email/resend', {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        setResendMessage('Verification email sent successfully!');
        setResendCooldown(60); // 1 minute cooldown
      } else {
        const data = await response.json();
        setResendMessage(data.error || 'Failed to send email');
      }
    } catch (error) {
      setResendMessage('Failed to send email');
    } finally {
      setResendLoading(false);
    }
  };

  if (emailVerified) {
    return (
      <Alert color="green" variant="light" icon={<IconCheck size={16} />}>
        Email verified successfully!
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed" style={{ margin: 0 }}>
        We&apos;ve sent a verification email to <strong>{userEmail}</strong>
      </Text>
      
      <Text size="sm" c="dimmed">
        Please check your email and click the verification link. After clicking the link, return to this tab and we&apos;ll automatically continue your application.
      </Text>
      
      <Text size="xs" c="dimmed" style={{ fontStyle: 'italic' }}>
        💡 Tip: Click the verification link in your email, then come back to this tab. We&apos;ll detect when you return and continue automatically.
      </Text>
      
      <Group gap="sm">
        <Button 
          variant="outline" 
          onClick={onCheckVerification} 
          size="sm"
          loading={loading}
        >
          I&apos;ve verified my email
        </Button>
        
        <Button 
          variant="light" 
          onClick={handleResendEmail} 
          size="sm"
          loading={resendLoading}
          disabled={resendCooldown > 0}
        >
          {resendCooldown > 0 ? `Resend (${resendCooldown}s)` : 'Resend email'}
        </Button>
      </Group>
      
      {resendMessage && (
        <Alert 
          color={resendMessage.includes('successfully') ? 'green' : 'red'} 
          variant="light"
          onClose={() => setResendMessage(null)}
          withCloseButton
        >
          {resendMessage}
        </Alert>
      )}
      
      {authError && (
        <Alert color="red" variant="light">
          {authError}
        </Alert>
      )}
    </Stack>
  );
}
